import { useEffect, useMemo, useRef, useState } from "react";
import { getPlatforms, IonLabel, IonLoading, IonToggle } from "@ionic/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AdditionalEditField } from "components/EditRequest/AdditionalEditsField";
import {
  BudgetField,
  DayOfContactField,
  filterBudgets,
  GradeField,
  InternalCodeField,
  isRovingTeacher,
  NotesToSubField,
  PositionField,
  SchoolField,
  SubjectField,
  SubSelect,
  TeacherField,
  TimeToAccept,
  useBudgets,
  useSubs,
} from "components/FormFields";
import { useSchool } from "components/FormFields/SchoolField/hooks";
import { PhoneInput } from "components/PhoneInput/PhoneInput";
import { DateTime, Settings } from "luxon";
import { PositionModel, RequestModel, SchoolModel, SubModel, TeacherKind } from "model";
import { useAuth } from "pages";
// FIXME: Replace with `Icon` from "swing-components"
import { IoHelpCircle } from "react-icons/io5";
import { useHistory } from "react-router-dom";
import { Alert, Card, Colors, formatDate, Icon, IconName, Tooltip } from "swing-components";
import { removeEmptyValues } from "swing-utils";

import { RequestFormDateField } from "~components/FormFields";
import {
  ALERT_COLOR_KEY,
  ALERT_DISCLAIMER_KEY,
  ALERT_MESSAGE_KEY,
  ALERT_TITLE_KEY,
  AlertState,
} from "~components/RequestInfoCard/constants";
import {
  api,
  COMBINED_RESTRICTIONS,
  getFeatureFlags,
  translate,
  useShouldRestrictAdmin,
  useURLSearchParams,
} from "~utils";

import { Button, Loader } from "..";
import {
  generateSelectableDaysFromExistingSelectableDays,
  getInitialIntervals,
  getNextValidDate,
  isDateWithin,
  isSameDate,
  TimesTableRowType,
  useTimeToAccept,
} from "./hooks";
import { fixStartAndEndTimes } from "./hooks/useRequestDate.utils";
import styles from "./RequestForm.module.css";
import {
  calculateCredentialRequired,
  diffKeys,
  filterClassroomUsersFromAdmins,
  SubScheduleDay,
} from "./utils";

/** Types */
type RequestFormProps = {
  request?: RequestModel;
  onModalSuccess?: () => void;
  onModalDismiss?: () => void;
  schools: Array<SchoolModel>;
  mode?: "edit" | "create";
};

/** Component **/
export default function RequestForm(props: RequestFormProps) {
  /*** Props ****/
  const { request, onModalSuccess, onModalDismiss, schools, mode } = props;

  /*** Constants ****/
  const isEditing = mode === "edit";
  const searchParams = useURLSearchParams();
  const clonedRequestId = searchParams.get("clonedFrom");
  // TODO: We should move this to the RequestDetailsView component since it's
  // not rendered in the form itself.
  const alertState: AlertState = {
    [ALERT_TITLE_KEY]: translate("REQUEST_ALERT_CREATED_HEADER"),
    [ALERT_MESSAGE_KEY]: translate("REQUEST_ALERT_CREATED_MESSAGE"),
  };

  /** Hooks **/
  const { userInfo } = useAuth();
  const history = useHistory();
  const queryClient = useQueryClient();

  /*** Mutations ***/
  const { mutate: createRequest, isPending: isSubmitPending } = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      const { data } = await api({
        method: "POST",
        url: "/api/admin/request",
        data: payload,
      });
      return data;
    },
    onSuccess: ({ data }) => {
      onModalSuccess?.();
      const isPendingLTRequest = new RequestModel(data).isLTRequestPending();

      const pendingLTRequestAlert = {
        [ALERT_TITLE_KEY]: translate("LABEL_PENDING_SWING_REVIEW_TITLE"),
        [ALERT_MESSAGE_KEY]: translate("LABEL_PENDING_SWING_REVIEW_MESSAGE"),
        [ALERT_COLOR_KEY]: "warning",
      } satisfies AlertState;

      history.push({
        pathname: `/active-requests/${data?.["sub-req/id"]}`,
        state: isPendingLTRequest ? pendingLTRequestAlert : alertState,
      });
    },
  });

  const { mutate: editRequest, isPending: isEditPending } = useMutation({
    mutationFn: async (payload: Record<string, unknown>) => {
      // Tells TS that `request` is always available in this case.
      if (!request) return;

      const { data } = await api({
        method: "POST",
        url: `/api/admin/request/${request.id}/edit`,
        data: payload,
      });
      return data;
    },
    onSuccess: ({ data }: { data: { "sub-req/id": string } }) => {
      // FIXME: This may not be working as we expect since the documentation
      // shows that it will invalidate based on the `queryKey` which is not
      // passed in. See https://tanstack.com/query/v4/docs/react/reference/QueryClient#queryclientinvalidatequeries
      queryClient.invalidateQueries();

      const filteredIntervals = initialIntervals.filter((interval) => interval.isEnabled);
      const difference = diffKeys(
        request?.getMultiDayRequestFields(filteredIntervals) ?? {},
        multiDayFormState,
      );
      const showUnderReviewAlert = hasAdditionalEdits || difference.includes("multiDayAdjustments");

      let title = translate("LABEL_EDIT_REQUEST_SUCCESS");

      // https://linear.app/swing-education/issue/MKTP-1662/bug-fix-success-message-for-edits-and-review-status-display
      // Show that request is under review IF and only IF, we are editing the additional edits field alone
      if (difference?.length === 1 && difference.includes("additionalEditMemo")) {
        title = translate("LABEL_EDITS_UNDER_REVIEW");
      }

      const historyObj = {
        pathname: `/active-requests/${data?.["sub-req/id"]}`,
        state: {
          [ALERT_TITLE_KEY]: title,
          // Instead, we will send it as a message
          [ALERT_MESSAGE_KEY]: difference,
          [ALERT_DISCLAIMER_KEY]: showUnderReviewAlert && translate("LABEL_ADDITIONAL_EDITS_ALERT"),
        },
      };
      // if on the same page and in a modal, we want to replace the history object
      if (onModalSuccess) {
        onModalSuccess();
        history.replace(historyObj);
      } else {
        // if on a different page, we want to push the history object
        history.push(historyObj);
      }
    },
    onError: () => {
      history.push(`/active-requests/${request?.id}`);
    },
  });

  const isClassroomUser = useShouldRestrictAdmin(COMBINED_RESTRICTIONS.CLASSROOM_ADMIN);
  const isAdminExtendRequestEnabled = getFeatureFlags(userInfo).EXTEND_WAITLISTED_FILLED_REQUESTS;

  // TODO: Improve type safety for formSubmissionErrObj & requiredFieldTouchedObj when a refactor of this form has commenced
  // These objects currently have many keys, and their values can be of various types.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [formSubmissionErrObj, setFormSubmissionErrObj] = useState<Record<string, any>>({});
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [requiredFieldTouchedObj, setRequiredFieldTouchedObj] = useState<Record<string, any>>({});

  // Initialize the the Request's School or the first School in the list
  // FIXME: There is a situation where the schools array is empty.
  // TODO: Fix in https://linear.app/swing-education/issue/MKTP-885
  const [schoolId, setSchoolId] = useState(request?.school?.id ?? schools[0].id);
  // FIXME: We are using `!` here but there could be a situation where the
  // School does not match.
  // TODO: Fix in https://linear.app/swing-education/issue/MKTP-885
  const school = schools.find((school) => school.id === schoolId)!;

  // Fetch school data with admin information
  const schoolWithAdmins = useSchool(schoolId);
  const schoolAdmins = schoolWithAdmins?.admins || [];
  const teachers = schoolWithAdmins?.teachers || [];

  // Filter out classroom users (teachers) from admins list for day of contact dropdown
  const admins = filterClassroomUsersFromAdmins(schoolAdmins, teachers);

  const requestorAdmin = { id: userInfo.id || "", name: userInfo.name || "" };

  /*** Luxon Settings ***/
  if (school?.tzString) {
    Settings.defaultZone = school?.tzString;
  }
  // This MUST be below the `Setting.defaultZone` setting for Luxon so that all
  // generated times are in the correct timezone.
  const NOW = DateTime.now().set({ second: 0, millisecond: 0 });

  const [intervals, setIntervals] = useState<Array<TimesTableRowType>>(
    getInitialIntervals({
      now: NOW,
      schoolHours: school.hours,
      schoolClosures: school.closures,
      intervals: request?.getIntervals(true),
    }),
  );

  const hasSubInPool = request?.pool && request?.pool?.length > 0;
  const isFilledOrWaitlisted = request?.isFilled() || hasSubInPool;

  const shouldUsePreviousWeekdayTimes = isAdminExtendRequestEnabled && isFilledOrWaitlisted;

  const { data: subSchedule } = useQuery({
    queryKey: ["sub", request?.sub.id, "schedule"],
    queryFn: async () => {
      const newURLParams = new URLSearchParams();

      // set the start and end date url params
      newURLParams.set("start", request?.endDate.toISO() ?? "");
      newURLParams.set("end", request?.endDate.plus({ year: 1 }).toISO() ?? "");

      const { data } = await api({
        method: "GET",
        url: `/api/admin/subs/${request?.sub.id}/schedule?${newURLParams.toString()}`,
      });
      return data;
    },
    select: (data: { data: Array<SubScheduleDay<string>> }) => {
      return data.data.map((date) => ({
        ...date,
        start: DateTime.fromISO(date.start),
        end: DateTime.fromISO(date.end),
      }));
    },
    enabled: Boolean(isAdminExtendRequestEnabled && request?.sub.id),
  });

  // See: https://linear.app/swing-education/issue/MKTP-1070/[swing-schools]-[bug]-disable-submit-edit-button-when-no-edits-are
  // We only want to cache the intervals once on initial render
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const initialIntervals = useMemo(() => intervals, []);

  const [multiDayAdjustmentNotes, setMultiDayAdjustmentNotes] = useState(
    request?.getIntervals() && request?.getIntervals().length > 15
      ? // NOTE: In situations where this field is mistakenly missing, we will
        // gracefully default it to an empty string which will trigger a form
        // invalidation if this is a long term request
        (request?.multiDayAdjustments ?? "")
      : "",
  );
  const [teacher, setTeacher] = useState(request?.teacher || "");
  const [grade, setGrade] = useState<string[]>(request?.gradesArray || userInfo.grades);
  const [subject, setSubject] = useState<string[]>(request?.subjectsArray || userInfo.subjects);
  // https://gitlab.com/swing-education/neo/-/merge_requests/1669
  // Used if specific teacher is chosen
  const [teacherId, setTeacherId] = useState(request?.teacherId || "");
  const [teacherKind, setTeacherKind] = useState<TeacherKind | "">(request?.teacher || "");
  const [contactPhone, setContactPhone] = useState<string>(
    request?.contactPhone || userInfo.phone || "",
  );
  const [dayOfContactAdminId, setDayOfContactAdminId] = useState<string>(
    request?.dayOfContactAdminId || (isClassroomUser ? "" : requestorAdmin.id),
  );
  const [internalCode, setInternalCode] = useState(request?.internalCode || "");
  const [isCredentialRequired, setIsCredentialRequired] = useState(
    request ? request?.isCredentialRequired : true,
  );
  const [isLessonPlanProvided, setLessonPlan] = useState(request?.isLessonPlanProvided || false);
  const [memo, setMemo] = useState<string>(request?.notes || school?.defaultNotes || "");
  const [subs, setSubs] = useState<Array<SubModel>>([]);
  const { timeToAccept, setTimeToAccept, timeToAcceptChoices } = useTimeToAccept(
    // TODO: Should we be forcing the `.at(0)` to not be undefined?
    intervals.filter((interval) => interval.isEnabled !== false).at(0)!.startTime,
    NOW,
  );

  // The position logic is needed here for if the Admin does not open the modal due to wanting to use the default
  const initialSelectedPosition =
    school?.getPosition(request?.position.id) || SchoolModel.getDefaultPosition(school?.positions);
  const [position, setPosition] = useState<PositionModel | undefined>(initialSelectedPosition);

  /** Budgets **/
  const { data: allBudgets } = useBudgets({
    showSuperadminBudgets: userInfo?.isSuperAdmin,
    includeSpend: true,
  });
  const [budgetId, setBudgetId] = useState<string | undefined>(request?.budget?.id);
  const budgets = filterBudgets(allBudgets, school!.id, school!.districtId);
  const hasBudgetsToShow = Boolean(budgets.length);

  // These values are not currently stored in Datomic so they won't have a value from the model
  const [hasAdditionalEdits, setHasAdditionalEdits] = useState(false);
  const [additionalEditMemo, setAdditionalEditMemo] = useState("");

  const showAdditionalEditField =
    request?.isFilled() || (request?.poolCount && request.poolCount > 0);

  // Handles the optional priority access section visibility and
  // makes sure it's scrolled into view when enabled.
  const priorityAccessRef = useRef<HTMLDivElement | null>(null);
  const [isPriorityAccessEnabled, setIsPriorityAccessEnabled] = useState(false);

  // FIXME: When duplicating a Request with "Priority Access" enabled, the page
  // is being scrolled. We should not be doing that on initial render.
  useEffect(() => {
    // Scroll to the priority access section once it becomes visible
    if (isPriorityAccessEnabled && priorityAccessRef.current) {
      priorityAccessRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [isPriorityAccessEnabled]);

  // TODO improve once we add a Form Framework
  function areRequiredFieldsValid() {
    const hasError = (v: unknown) => Boolean(v);

    if (memo.trim().length === 0) {
      return false;
    }

    if (hasBudgetsToShow && budgetId === undefined) {
      return false;
    }

    if (Object.values(formSubmissionErrObj).some(hasError)) {
      return false;
    }

    if (school.isInternalCodeRequired && internalCode.trim() === "") {
      return false;
    }

    // Must have a valid admin selected for day of contact if school has admins and user is super admin
    if (schoolAdmins.length > 0 && userInfo.isSuperAdmin && dayOfContactAdminId.trim() === "") {
      return false;
    }

    // If priority access is enabled, ensure a sub is selected
    if (isPriorityAccessEnabled && subs.length === 0) {
      return false;
    }

    // TimeToAccept is a required field (indicated to admins with an asterisk),
    // but it always has a default value selected. Therefore, we do not add it
    // to the error object or display any validation message.

    return true;
  }

  const multiDayFormState = {
    school: schoolId,
    teacher,
    grade,
    subject,
    contactPhone,
    dayOfContactAdminId,
    internalCode,
    isCredentialRequired,
    isLessonPlanProvided,
    memo,
    budgetId,
    position: position?.id,
    additionalEditMemo,
    intervals,
    multiDayAdjustments: multiDayAdjustmentNotes,
  };

  const customizedAdditionalEditMemo =
    isAdminExtendRequestEnabled && isFilledOrWaitlisted
      ? getExtendedRequestEdits({
          additionalEditMemo,
          intervals,
          initialIntervals,
          subSchedule,
        })
      : undefined;

  const filledRequestFormState = {
    contactPhone,
    dayOfContactAdminId,
    internalCode,
    memo,
    additionalEditMemo,
    intervals,
    budgetId,
    school: schoolId,
    isLessonPlanProvided,
    teacher,
    grade,
    subject,
  };

  function isFormChanged() {
    function didFieldsChange(
      beforeEdit: Record<string, unknown>,
      afterEdit: Record<string, unknown>,
    ) {
      // Trim string fields to prevent false diffs from extra spaces
      function normalizeStringFields(obj: Record<string, unknown>) {
        return {
          ...obj,
          memo: typeof obj.memo === "string" ? obj.memo.trim() : "",
          internalCode: typeof obj.internalCode === "string" ? obj.internalCode.trim() : "",
        };
      }
      const a = removeEmptyValues(normalizeStringFields(beforeEdit));
      const b = removeEmptyValues(normalizeStringFields(afterEdit));

      return diffKeys(a, b).length > 0;
    }

    if (!isEditing) {
      return false;
    }

    if (request?.isFilled()) {
      return didFieldsChange(
        request?.getFilledRequestFields(initialIntervals),
        filledRequestFormState,
      );
    }

    // Handle comparing multi day form state
    else {
      return didFieldsChange(
        request?.getMultiDayRequestFields(initialIntervals) ?? {},
        multiDayFormState,
      );
    }
  }

  function onSubmit(_intervals: TimesTableRowType[]) {
    if (!school) return;

    // TODO: Determine why are getting rid of the `!isEnabled` times for the
    // UI here. We should want to keep the UI the same as the user has and
    // only remove the `!isEnabled` times when we are submitting the request.
    setIntervals(_intervals.filter((time) => time.isEnabled));

    // Handle multi day adjustment notes
    let submissibleMDAdjustments: string | null | undefined;
    // If there are more than 15 days between the start and end date
    if (intervals.length > 15) {
      // If the new Long Term Request UI is enabled
      // Then handle an empty string, `""`, as undefined
      submissibleMDAdjustments =
        multiDayAdjustmentNotes.length > 0 ? multiDayAdjustmentNotes : undefined;
    }
    // Otherwise, if the Request is not a Long Term Request
    else {
      // Then ignore the multi day adjustment notes and replace it with null
      submissibleMDAdjustments = null;
    }

    const submissibleIntervals = _intervals
      .filter((item) => item.isEnabled)
      // filter out any intervals that conflict with sub schedule
      .filter(
        (item) =>
          !subSchedule?.some((conflictDay) => isSameDate(conflictDay.start, item.startTime)),
      )
      .map(({ startTime, endTime }) => {
        startTime.setZone(school?.tzString);
        return {
          // TODO: Document why we are not using the `dateString` here.
          start: startTime.toString(),
          end: endTime.toString(),
          offset: Math.abs(startTime.offset),
        };
      });

    const credentialRequiredCalculated = calculateCredentialRequired(school, isCredentialRequired);

    const requestPayload = {
      ...removeEmptyValues({
        admin: userInfo.id,
        intervals: submissibleIntervals,
        school: schoolId,
        "credential-required?": credentialRequiredCalculated,
        "lesson-plan?": isLessonPlanProvided,
        memo,
        // Only include selected subs in payload if Priority Access is enabled
        ...(isPriorityAccessEnabled ? { "preferred-subs": subs.map((sub) => sub.id) } : {}),
        "multi-day-adjustments": submissibleMDAdjustments,
        ...(isPriorityAccessEnabled &&
        timeToAccept === translate("TIME_TO_ACCEPT_ONLY_SELECTED_SUBS_VALUE")
          ? { "only-preferred-can-access": true }
          : {}),
        // https://linear.app/swing-education/issue/MKTP-397/[fe]-create-request-page-general-pool-delay-being-sent-when-priority
        // Do NOT send general-pool-delay property if priority access is disabled
        ...(isPriorityAccessEnabled && typeof timeToAccept === "number"
          ? { "general-pool-delay": timeToAccept }
          : {}),
        timestamp: DateTime.now().toString(),
        position: position?.id || undefined,
        // https://linear.app/swing-education/project/swing-schools-extend-filled-waitlisted-requests-24fa9b608736#comment-22d88119
        // If the admin is extending a filled/waitlisted request, then we want to send the extra edits
        "extra-edits": customizedAdditionalEditMemo ?? additionalEditMemo,
        "budget-id": budgetId,
        tags: getPlatforms(),
      }),
      // Purposely not removing empty values since an empty string represents
      // that the field should be removed.
      teacher: teacherId || teacher,
      "internal-code": internalCode,
      "contact-number": contactPhone,
      "day-of-contact-admin-id": dayOfContactAdminId,
      grade: grade.join(", "),
      subject: subject.join(", "),
      ...(mode === "create" && clonedRequestId && { clonedFrom: clonedRequestId }),
    };

    if (isEditing) {
      editRequest(requestPayload);
    } else {
      createRequest(requestPayload);
    }
  }

  useEffect(() => {
    if (requiredFieldTouchedObj.memo) {
      setFormSubmissionErrObj((error: Record<string, unknown>) => ({
        ...error,
        memo: memo.trim() === "" ? translate("NOTES_TO_SUB_REQ_MEMO_ERROR") : undefined,
      }));
    }
  }, [memo, requiredFieldTouchedObj.memo]);

  const subSelectRequest = useMemo(() => {
    // Given that are are storing times in two formats, one for Single Day and
    // another for Multi Day requests, we must use this ternary to return the
    // correct start and end datetime.
    const start = intervals.filter((interval) => interval.isEnabled)?.at(0)?.startTime;
    const end = intervals.filter((interval) => interval.isEnabled)?.at(-1)?.endTime;

    return {
      school: schoolId,
      // NOTE: Including `?? 0` to keep TS happy since this field is required
      // but the `school` variable could be `undefined`.
      // FIXME: Why are we using `.abs` here, it's breaking the offset since
      // you can have negative offsets as well as positive offsets from UTC.
      offset: Math.abs(school?.timeZone ?? 0),
      start: start!.toISO(),
      end: end!.toISO(),
    };
  }, [schoolId, school, intervals]);

  const { subs: preferredSubs } = useSubs({
    query: "",
    request: subSelectRequest,
    enabled: !!request,
  });
  const copiedPreferredSubs = useMemo(
    () => preferredSubs?.filter((sub) => request?.preferredSubs?.includes(sub.id)),
    [preferredSubs, request?.preferredSubs],
  );

  useEffect(() => {
    if (
      copiedPreferredSubs &&
      copiedPreferredSubs.length > 0 &&
      isPriorityAccessEnabled === false
    ) {
      // We only want to set these if the current school id matches the one from
      // the duplicate request, otherwise, set as blank
      if (request?.school.id === schoolId) {
        setSubs(copiedPreferredSubs);
        setIsPriorityAccessEnabled(true);
      }
    }
    // We are purposefully ignoring changes to `isPriorityAccessEnabled` since
    // we only want this code to trigger once on render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [copiedPreferredSubs]);

  /*** Handlers ***/
  /**
   * When changing schools, keep the start and end date, but reset the selected
   * days so that school closures can be handled correctly.
   */
  function handleSchoolChange(params: { newSchoolId: string }) {
    const { newSchoolId } = params;

    const currentSelectedSchool = schools.find((school) => school.id === newSchoolId)!;

    if (currentSelectedSchool?.defaultNotes) {
      setMemo(request?.notes || currentSelectedSchool.defaultNotes || "");
    }
    setSchoolId(newSchoolId);
    setPosition(SchoolModel.getDefaultPosition(currentSelectedSchool?.positions));
    setBudgetId(undefined);
    setTeacher("");
    setSubs([]);
    setTeacherId("");
    setIsPriorityAccessEnabled(false);
    setTeacherKind("");
    setInternalCode("");
    setFormSubmissionErrObj({});

    // Regenerate the selected days given the new school
    const newSchool = schools.find((school) => school.id === newSchoolId)!;
    setIntervals(() =>
      generateSelectableDaysFromExistingSelectableDays({
        now: NOW,
        startDate: getIntervalStartDate(intervals),
        endDate: getIntervalEndDate(intervals),
        selectableDays: [],
        schoolHours: newSchool.hours,
        schoolClosures: newSchool.closures,
        shouldUsePreviousWeekdayTimes,
      }),
    );
  }

  function handleTeacherChange(teacherSelection: string) {
    // Don't apply any changes if the value hasn't changed
    if (teacherSelection === teacher) return;

    const rovingMessage = translate("NOTES_ROVING_MESSAGE");
    const touchMemo = () => {
      setRequiredFieldTouchedObj((touched: Record<string, unknown>) => ({
        ...touched,
        memo: true,
      }));
    };

    setTeacher(teacherSelection);

    if (!isRovingTeacher(teacherSelection as TeacherKind)) {
      setMemo((prevMemo) => `${prevMemo.replace(rovingMessage, "").trim()}`);
      touchMemo();
      setGrade([]);
      setSubject([]);
    } else {
      if (!memo.includes(rovingMessage)) {
        setMemo((prevMemo) => `${rovingMessage} \n\n ${prevMemo}`.trim());
        touchMemo();
      }
      setGrade([translate("UNKNOWN")]);
      setSubject([translate("UNKNOWN")]);
    }
  }

  function onEditCancel() {
    onModalDismiss?.();
  }

  /**
   * Triggered when changing the Start or End date of the Request.
   * This is not triggered when toggling a day, or changing a day's start or
   * end time.
   */
  function handleDateChange(params: { startDate?: DateTime; endDate?: DateTime }) {
    let { startDate, endDate } = params;

    // If the start date has changed and is after the end date
    if (startDate && startDate > getIntervalEndDate(intervals)) {
      // Then update the end date to be the start date
      endDate = startDate.endOf("day");
    }
    // If end date has changed and is before the start date
    else if (endDate && endDate < getIntervalStartDate(intervals)) {
      // Then update the start date to be the end date
      startDate = endDate.startOf("day");
    }

    // Generate and set the new intervals given the new start and end date time
    setIntervals((oldSelectableDays) =>
      generateSelectableDaysFromExistingSelectableDays({
        now: NOW,
        startDate: startDate ?? getIntervalStartDate(intervals),
        endDate: endDate ?? getIntervalEndDate(intervals),
        selectableDays: oldSelectableDays,
        schoolHours: school.hours,
        schoolClosures: school.closures,
        shouldUsePreviousWeekdayTimes,
      }),
    );
  }

  /**
   * Triggered when a day is toggled or the start/end time has changed.
   * This is not triggered when changing the start and end date.
   */
  function handleDateTimeChange(newTimesTables: Array<TimesTableRowType>) {
    setIntervals((oldIntervals) =>
      fixStartAndEndTimes({ oldIntervals, newIntervals: newTimesTables, now: NOW }),
    );
  }

  /**
   * Triggered when clicking the "Add another day" button and adds a new
   * new interval to the Request. The next interval will be a valid weekday
   * which currently means not a weekend.
   */
  function handleAddAnotherDay() {
    const currentStartDate = intervals[0].startTime.startOf("day");
    const currentEndDate = intervals[intervals.length - 1].endTime.endOf("day");

    const newEndDate = getNextValidDate({
      date: currentEndDate,
      schoolClosures: school.closures,
      subSchedule,
    });

    // Generate and set the new intervals given the new start and end date time
    setIntervals((oldSelectableDays) =>
      generateSelectableDaysFromExistingSelectableDays({
        now: NOW,
        startDate: currentStartDate,
        endDate: newEndDate,
        selectableDays: oldSelectableDays,
        schoolHours: school.hours,
        schoolClosures: school.closures,
        shouldUsePreviousWeekdayTimes,
      }),
    );
  }

  /** Handle changes to the "Additional Edits" field. */
  function handleAdditionalEditsChange(additionalEdits: string) {
    setMultiDayAdjustmentNotes(additionalEdits);
  }

  /*** Render #1 ***/
  /** Show loading icon if we are copying a request, have saved preferred subs
   *  but don't have the response back yet.
   *  We also want to make sure are in create mode (RequestASub) */
  if (request && request.preferredSubs?.length && !copiedPreferredSubs && mode === "create") {
    return <Loader />;
  }

  /*** Constants #2 ***/
  const isRequestWithinCancelWindow =
    request &&
    isDateWithin(
      request.startTime.minus({ hours: request.school.cancelPolicy }),
      request.startTime,
      NOW,
    );

  const showCancelWindowAlert = isEditing && !request?.isFilled() && isRequestWithinCancelWindow;

  const hideCredentialToggle = isEditing
    ? school?.hideCredentialToggle || !request?.isCredentialRequired
    : school?.hideCredentialToggle;
  const showOptionalPriorityAccess = !isEditing && !request?.isFilled() && !hasSubInPool;

  /*** Render #2 ***/
  return (
    <div
      className="mw-800"
      css={{
        display: "flex",
        flexDirection: "column",
        gap: 24,
      }}
    >
      {isAdminExtendRequestEnabled && isFilledOrWaitlisted && (
        <Alert
          color="warning"
          showIcon
          // TODO: Remove conditional when admin edit is released
          title={translate(
            request?.isFilled()
              ? "EDIT_REQUEST_FILLED_WARNING_HEADER"
              : "EDIT_REQUEST_FILLED_WAITLISTED_WARNING_HEADER",
          )}
        >
          {/* TODO: Remove conditional when admin edit is released */}
          {translate(
            request?.isFilled()
              ? "EDIT_REQUEST_FILLED_WAITLISTED_WARNING_MESSAGE"
              : "EDIT_REQUEST_FILLED_WARNING_MESSAGE",
            {
              isFilled: !!request?.isFilled(),
            },
          )}
        </Alert>
      )}

      {/* FIXME: Determine if we want to keep this as a pending action loading
      patter moving forward. */}
      <IonLoading isOpen={isSubmitPending || isEditPending} message="Submitting Request..." />

      {/* Step #1: Select Schools */}
      <Card>
        <SchoolField
          value={schoolId}
          onChange={(schoolId) => handleSchoolChange({ newSchoolId: schoolId })}
          schools={schools}
          isDisabled={!userInfo.isSuperAdmin && (isEditing || request?.isFilled() || hasSubInPool)}
        />
      </Card>
      {/* Step #2: Select Dates & Times */}
      {/* TODO: Create storybook to test this out */}
      <Card>
        <RequestFormDateField
          now={NOW}
          school={school}
          intervals={intervals}
          // MultiDateField
          onDateChange={handleDateChange}
          // DayField
          onAddAnotherDay={handleAddAnotherDay}
          // MultiDateTimeField - MultiDay
          onDateTimeChange={handleDateTimeChange}
          // MultiDateTimeField - Long Term
          multiDayAdjustmentNotes={multiDayAdjustmentNotes}
          onMultiDayAdjustmentNotesChange={handleAdditionalEditsChange}
          // MultiDateField and DayField
          isWaitlistedOrFilledReq={request?.isFilled() || hasSubInPool}
          originalEndDate={request?.endDate}
          subSchedule={subSchedule}
          isExtendedRequestEnabled={isAdminExtendRequestEnabled}
        />
      </Card>
      {/* Step #3 */}
      <Card>
        <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
          {/* Step #3.1: Positions */}
          {/* TODO: Test this */}
          {school?.positions && position && (
            <>
              <PositionField
                positions={school.positions}
                selectedPosition={position}
                onChange={setPosition}
                isDisabled={request?.isFilled() || hasSubInPool}
              />
              {/* Divider: TODO: Do we have this */}
              <div css={{ height: 1, background: Colors.slate200 }} />
            </>
          )}

          {/* Step #3.2: Teacher */}
          {!isClassroomUser && (
            <>
              <TeacherField
                value={teacher}
                teachers={(school as SchoolModel).teachers}
                teacherKind={teacherKind as TeacherKind}
                setTeacherKind={setTeacherKind as () => void}
                setTeacherId={setTeacherId}
                onChange={(teacherSelection) => handleTeacherChange(teacherSelection)}
                isDisabled={!userInfo.isSuperAdmin && (request?.isFilled() || hasSubInPool)}
              />
              <div css={{ height: 1, background: Colors.slate200 }} />
            </>
          )}

          {/* Step #3.3: Grade */}
          <>
            <GradeField
              initialGradeSelected={grade}
              onChange={(val) => setGrade(val)}
              isDisabled={!userInfo.isSuperAdmin && (request?.isFilled() || hasSubInPool)}
            />
            <div css={{ height: 1, background: Colors.slate200 }} />
          </>

          {/* Step #3.4: Subject */}
          <>
            <SubjectField
              subjectValues={subject}
              onChange={setSubject}
              isDisabled={!userInfo.isSuperAdmin && (request?.isFilled() || hasSubInPool)}
            />
            <div css={{ height: 1, background: Colors.slate200 }} />
          </>

          {/* Step #3.5: Contact Number - Show by default for non-super admins, or when schoolAdmins is empty for super admins */}
          {(!userInfo.isSuperAdmin || (userInfo.isSuperAdmin && schoolAdmins.length === 0)) && (
            <div
              css={{
                display: "flex",
                flexDirection: "column",
                gap: 8,
                // This is to remove the default height of the `ion-input` from
                // being 42px.
                ".sc-ion-input-md-h": {
                  minHeight: "auto !important",
                },
              }}
            >
              <IonLabel className="font-medium">{translate("LABEL_CONTACT_NUMBER")}</IonLabel>
              <PhoneInput value={contactPhone} onIonInput={setContactPhone} />
            </div>
          )}

          {/* Step #3.6: Day of Contact - Show when schoolAdmins is NOT empty and user is super admin */}
          {schoolAdmins.length > 0 && (
            <DayOfContactField
              onChange={setDayOfContactAdminId}
              admins={admins}
              defaultAdmin={
                isEditing
                  ? // Edit mode: Find existing admin or default to empty
                    admins.find((admin) => admin.id === dayOfContactAdminId) || { id: "", name: "" }
                  : // Create mode: Default based on classroom user status
                    isClassroomUser
                    ? { id: "", name: "" }
                    : requestorAdmin
              }
              isDisabled={!!(request?.isFilled() || hasSubInPool)}
              isRequired={true}
            />
          )}

          {/* Divider: TODO: Do we have this */}
          <div css={{ height: 1, background: Colors.slate200 }} />

          {/* Step #3.6: Internal Code */}
          <InternalCodeField
            internalCode={internalCode}
            setInternalCode={setInternalCode}
            formSubmissionErrObj={formSubmissionErrObj}
            required={school.isInternalCodeRequired}
            onFormSubmissionError={(errorObj) => {
              setFormSubmissionErrObj(errorObj);
            }}
            onRequiredFieldTouched={(touchedObj) => {
              setRequiredFieldTouchedObj(touchedObj);
            }}
          />
          {/* Divider: TODO: Do we have this */}
          <div css={{ height: 1, background: Colors.slate200 }} />

          {/* Step #3.7: Budget */}
          {hasBudgetsToShow && (
            <>
              <BudgetField
                budgets={budgets}
                selectedBudgetId={budgetId}
                onConfirm={setBudgetId}
                formSubmissionErrObj={formSubmissionErrObj}
                onFormSubmissionError={setFormSubmissionErrObj}
                isDisabled={!userInfo.isSuperAdmin && (request?.isFilled() || hasSubInPool)}
              />

              {/* Divider: TODO: Do we have this */}
              <div css={{ height: 1, background: Colors.slate200 }} />
            </>
          )}

          {/* Step #3.8: Credential Required */}
          {/* Question: Credential / Perming Required? */}
          {!hideCredentialToggle && (
            <>
              {/*
               * Don't allow admin to edit an NC request to C,
               * but allow C to NC.
               */}
              <div
                css={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  color: request?.isFilled() || hasSubInPool ? Colors.slate300 : undefined,
                }}
              >
                <IonLabel className="font-medium">
                  {translate("REQUEST_FORM_QUESTION_CREDENTIAL_LABEL")}
                </IonLabel>
                <IonToggle
                  aria-label="Credential/Permit requirement toggle"
                  checked={isCredentialRequired}
                  disabled={request?.isFilled() || hasSubInPool}
                  onIonChange={(e: CustomEvent) => {
                    setIsCredentialRequired(e.detail.checked);

                    if (e.detail.checked) {
                      const credentialedSubs = subs.filter((sub) => sub.isCredentialed);
                      setSubs(credentialedSubs);
                    }
                  }}
                  slot="end"
                />
              </div>

              {/* Divider: TODO: Do we have this */}
              <div css={{ height: 1, background: Colors.slate200 }} />
            </>
          )}

          {/* Step #3.9: Lesson Plan */}
          <>
            <div
              css={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <div className="flex items-center">
                <IonLabel className="font-medium">
                  {translate("REQUEST_FORM_QUESTION_LESSON_PLAN_LABEL")}
                </IonLabel>
                <button id="internal-code-tooltip" className={styles.unstyledButton}>
                  <IoHelpCircle className={`icon-lg ${styles.helpIcon}`} />
                </button>
                <Tooltip
                  trigger="internal-code-tooltip"
                  header="Lesson Plan Provided?"
                  content="Will your Swing Sub be provided with lesson plans upon arrival? Providing lesson plans will help your Swing Sub be better prepared."
                />
              </div>
              <IonToggle
                aria-label="Lesson Plan Provided toggle"
                slot="end"
                checked={isLessonPlanProvided}
                onIonChange={(e: CustomEvent) => setLessonPlan(e.detail.checked)}
              />
            </div>

            {/* Divider: TODO: Do we have this */}
            <div css={{ height: 1, background: Colors.slate200 }} />
          </>

          {/* Step #3.9: Notes to Sub */}
          {/* Show an alert when selecting "Special Education" */}
          {subject.includes("SPED") && (
            <Alert showIcon color="info" title={translate("NOTES_TO_SUB_SPED_ALERT_TITLE")}>
              <p
                css={{ margin: 0 }}
                dangerouslySetInnerHTML={{ __html: translate("NOTES_TO_SUB_SPED_ALERT_LIST") }}
              />
            </Alert>
          )}

          <NotesToSubField
            memo={memo}
            onChange={setMemo}
            formSubmissionErrObj={formSubmissionErrObj}
            onFormSubmissionError={(errorObj) => {
              setFormSubmissionErrObj(errorObj);
            }}
            onRequiredFieldTouched={(touchedObj) => {
              setRequiredFieldTouchedObj(touchedObj);
            }}
          />

          {/* Step #3.10: Additional Edits */}
          {!!showAdditionalEditField && (
            <>
              {/* Divider: TODO: Do we have this */}
              <div css={{ height: 1, background: Colors.slate200 }} />

              <AdditionalEditField
                hasAdditionalEdits={hasAdditionalEdits}
                additionalEditMemo={additionalEditMemo}
                setHasAdditionalEdits={(e: boolean): void => {
                  setHasAdditionalEdits(e);
                }}
                setAdditionalEditMemo={(e: string): void => {
                  setAdditionalEditMemo(e);
                }}
              />
            </>
          )}
        </div>
      </Card>
      {/* Step #4: Priority Access */}
      {showOptionalPriorityAccess && (
        <Card>
          <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div css={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <div css={{ display: "flex", alignItems: "center", gap: 8 }}>
                <h3 css={{ margin: 0, lineHeight: "16px" }}>{translate("SUB_SELECT_TITLE")}</h3>
                <button id="priority-access" className={styles.unstyledButton}>
                  <IoHelpCircle className={`icon-lg ${styles.helpIcon}`} />
                </button>
              </div>
              <Tooltip
                trigger="priority-access"
                header="How priority access works"
                positionAlign="page-center"
                content={<PriorityAccessTooltipContent />}
              />
              <IonToggle
                aria-label="Optional priority access toggle"
                checked={isPriorityAccessEnabled}
                onIonChange={(e) => {
                  setIsPriorityAccessEnabled(e.detail.checked);
                  // Remove the 'subs' error if the priority toggle is turned off,
                  // allowing the form to be submitted without requiring a selected sub.
                  if (!e.detail.checked && formSubmissionErrObj.subs) {
                    setFormSubmissionErrObj((prevErrors) => {
                      const updatedErrors = { ...prevErrors };
                      delete updatedErrors.subs;
                      return updatedErrors;
                    });
                  }
                }}
                slot="end"
              />
            </div>

            {isPriorityAccessEnabled && (
              <>
                {/* Divider: TODO: Do we have this */}
                <div css={{ height: 1, background: Colors.slate200 }} />

                <div
                  ref={priorityAccessRef}
                  css={{ display: "flex", flexDirection: "column", gap: 16 }}
                >
                  <SubSelect
                    model={subs}
                    request={subSelectRequest}
                    hideCredentialToggle={hideCredentialToggle}
                    initialSelection={{
                      selectedSubs: subs,
                      isCredentialRequired,
                      isCredentialedPositionSelected: position?.credsRequired,
                    }}
                    onConfirm={({ selectedSubs, isCredentialRequired }) => {
                      setSubs(selectedSubs);
                      setIsCredentialRequired(isCredentialRequired);
                    }}
                    formSubmissionErrObj={formSubmissionErrObj}
                    onFormSubmissionError={(error) => {
                      setFormSubmissionErrObj(error);
                    }}
                  />

                  {/* Divider: TODO: Do we have this */}
                  <div css={{ height: 1, background: Colors.slate200 }} />

                  <TimeToAccept
                    value={timeToAccept}
                    choices={timeToAcceptChoices}
                    onChange={setTimeToAccept}
                  />

                  {/* Divider: TODO: Do we have this */}
                  <div css={{ height: 1, background: Colors.slate200 }} />

                  <p css={{ margin: 0 }}>
                    We'll notify subs about this opportunity as soon as you submit. Review your
                    request before posting.
                  </p>
                </div>
              </>
            )}
          </div>
        </Card>
      )}
      {/* Step #5: Submit */}
      <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
        {showCancelWindowAlert && (
          <>
            <Alert color="success">
              <div className="flex w-full">
                <div>{translate("CANCELLATION_WINDOW_LABEL")}</div>
                <button id="present-cancellation-alert" className={styles.unstyledButton}>
                  <IoHelpCircle className={`icon-lg ${styles.helpIcon}`} />
                </button>
                <Tooltip
                  trigger="present-cancellation-alert"
                  content={translate("CANCELLATION_WINDOW_TOOLTIP_MESSAGE")}
                />
              </div>
            </Alert>
          </>
        )}

        {isEditing ? (
          <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <Button
              type="PRIMARY"
              // If the admin is extending a filled/waitlisted request AND adding an additional edit
              // then we want to send the original intervals (start and end date) and let CX change the
              // dates manually
              onClick={() => onSubmit(customizedAdditionalEditMemo ? initialIntervals : intervals)}
              isDisabled={!areRequiredFieldsValid() || !isFormChanged()}
              isFluid
            >
              {translate("EDIT_REQUEST_SUBMIT_LABEL")}
            </Button>
            <Button type="SECONDARY" onClick={onEditCancel} isFluid>
              {translate("EDIT_REQUEST_ACCEPTED_CANCEL_LABEL")}
            </Button>
          </div>
        ) : (
          <Button
            type="PRIMARY"
            onClick={() => onSubmit(intervals)}
            isDisabled={!areRequiredFieldsValid() || isEditPending || isSubmitPending}
            isFluid
          >
            {translate("LABEL_REQUEST_SUB")}
          </Button>
        )}
      </div>
    </div>
  );
}

/********** Helper Components **********/
function PriorityAccessTooltipContent() {
  const items: Array<{ icon: IconName; title: string; description: string }> = [
    {
      icon: "Groups",
      title: "Select specific subs",
      description: "Choose specific subs from your favorites and more.",
    },
    {
      icon: "Eye On",
      title: "Control who can see and sign up first",
      description: "Selected subs will be able to see and sign up for your request first.",
    },
    {
      icon: "Clock",
      title: "Decide when to open to all eligible subs",
      description:
        "Choose how long selected subs have to sign up before opening to all eligible subs.",
    },
  ];

  return (
    <div css={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {items.map(({ icon, title, description }, idx) => (
        <div key={`${icon}-${idx}`} css={{ display: "flex", gap: 16 }}>
          <Icon name={icon} />
          {/* Slight padding adjustment to align text w/ icon */}
          <div css={{ paddingTop: 2 }}>
            <p
              css={{
                margin: "0 0 4px",
                fontWeight: 500,
                lineHeight: "20px",
              }}
            >
              {title}
            </p>
            <p css={{ margin: 0 }}>{description}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/***** Helper Functions *****/
/**
 * Returns the toISODate equivalent using the startOf day.
 *
 * TODO: This is ignoring disabled days, could this affect us in it's usages?
 * Investigate in https://linear.app/swing-education/issue/MKTP-946
 */
function getIntervalStartDate(intervals: Array<TimesTableRowType>) {
  return intervals
    .filter((interval) => interval.isEnabled)
    .at(0)!
    .startTime.startOf("day");
}

/**
 * Returns the toISODate equivalent using the endOf day.
 *
 * TODO: This is ignoring disabled days, could this affect us in it's usages?
 * Investigate in https://linear.app/swing-education/issue/MKTP-946
 */
function getIntervalEndDate(intervals: Array<TimesTableRowType>) {
  return intervals
    .filter((interval) => interval.isEnabled)
    .at(-1)!
    .endTime.endOf("day");
}

type ExtendedRequestEditsParams = {
  initialIntervals: TimesTableRowType[];
  intervals: TimesTableRowType[];
  subSchedule?: SubScheduleDay[];
  additionalEditMemo: string;
};

/**
 * For the "Admin Extend Filled/Waitlisted Requests" feature
 * https://linear.app/swing-education/project/swing-schools-extend-filled-waitlisted-requests-24fa9b608736/overview#heading-additional-edits-f3916d2a
 *
 * @returns @type string The extended request edits.
 */
export function getExtendedRequestEdits(params: ExtendedRequestEditsParams) {
  const { additionalEditMemo, subSchedule = [], intervals, initialIntervals } = params;

  // https://linear.app/swing-education/issue/MKTP-1692/exclude-unchecked-non-school-days-from-support-memo
  // filter out non-school and disabled days
  const enabledDays = intervals.filter((interval) => interval.isEnabled);
  // And filter out conflicting days
  const nonConflictingDays = enabledDays.filter(
    (day) => !subSchedule.some((sub) => isSameDate(day.startTime, sub.start)),
  );
  // Get all of the new dates
  const newDates =
    nonConflictingDays?.filter((interval) => !initialIntervals.includes(interval)) || [];

  if (additionalEditMemo.length === 0 || newDates.length === 0) {
    return undefined;
  }

  const newDatesString = newDates.map((interval) => interval.dateString);

  const extraEditsString = `
    Admin wants to add the following dates/times:
    ${newDatesString.map(
      (date) => `
      - ${formatDate({ date, format: "Wed, Jan 1, 2025" })}`,
    )}

    This is what they wrote: ${additionalEditMemo}
  `;

  return extraEditsString;
}
